import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Set worker source for react-pdf v10
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PDF Document Builder - DocuSign-like interface
 * Allows uploading PDF, placing fillable fields, and sending to clients
 */
function PDFDocumentBuilder({ onSave, preparerId, companyId, existingTemplate, onClose }) {
  // eslint-disable-next-line no-unused-vars
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfData, setPdfData] = useState(null); // Base64 PDF data
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [pdfError, setPdfError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  const containerRef = useRef(null);
  const pageRef = useRef(null);

  // Field types available
  const fieldTypes = [
    { type: 'text', label: 'Text Field', icon: '📝', color: '#3b82f6' },
    { type: 'signature', label: 'Signature', icon: '✍️', color: '#8b5cf6' },
    { type: 'date', label: 'Date', icon: '📅', color: '#10b981' },
    { type: 'checkbox', label: 'Checkbox', icon: '☑️', color: '#f59e0b' },
    { type: 'initials', label: 'Initials', icon: '🔤', color: '#ec4899' },
    { type: 'name', label: 'Full Name', icon: '👤', color: '#06b6d4' },
    { type: 'email', label: 'Email', icon: '📧', color: '#84cc16' },
    { type: 'phone', label: 'Phone', icon: '📞', color: '#f97316' },
    { type: 'ssn', label: 'SSN (Last 4)', icon: '🔒', color: '#ef4444' },
    { type: 'address', label: 'Address', icon: '🏠', color: '#6366f1' },
  ];

  // Load existing template if editing
  useEffect(() => {
    if (existingTemplate) {
      setTemplateName(existingTemplate.name || '');
      setFields(existingTemplate.fields || []);
      if (existingTemplate.pdfData) {
        setPdfData(existingTemplate.pdfData);
      }
    }
  }, [existingTemplate]);

  // Handle PDF file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setPdfError(null);
      setPdfLoading(true);
      
      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        setPdfData(e.target.result);
        setPdfLoading(false);
      };
      reader.onerror = () => {
        setPdfError('Failed to read PDF file');
        setPdfLoading(false);
      };
      reader.readAsDataURL(file);
      
      setFields([]); // Reset fields when new PDF uploaded
      setCurrentPage(1);
    } else {
      alert('Please upload a valid PDF file');
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfError('Failed to load PDF. Please try a different file.');
    setPdfLoading(false);
  };

  // Add new field to canvas
  const addField = (fieldType) => {
    const newField = {
      id: `field_${Date.now()}`,
      type: fieldType.type,
      label: fieldType.label,
      page: currentPage,
      x: 50,
      y: 50,
      width: fieldType.type === 'checkbox' ? 20 : fieldType.type === 'signature' ? 200 : 150,
      height: fieldType.type === 'checkbox' ? 20 : fieldType.type === 'signature' ? 50 : 25,
      required: true,
      placeholder: `Enter ${fieldType.label.toLowerCase()}...`,
      color: fieldType.color,
      filledBy: 'client' // 'client' or 'preparer'
    };
    setFields([...fields, newField]);
    setSelectedField(newField.id);
  };

  // Handle field dragging
  const handleFieldMouseDown = (e, fieldId) => {
    e.stopPropagation();
    setSelectedField(fieldId);
    setIsDragging(true);
    
    const field = fields.find(f => f.id === fieldId);
    if (field) {
      setDragStart({
        x: e.clientX - field.x * scale,
        y: e.clientY - field.y * scale
      });
    }
  };

  // Handle resize start
  const handleResizeMouseDown = (e, fieldId, direction) => {
    e.stopPropagation();
    setSelectedField(fieldId);
    setIsResizing(true);
    setResizeDirection(direction);
    
    const field = fields.find(f => f.id === fieldId);
    if (field) {
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: field.width,
        height: field.height,
        fieldX: field.x,
        fieldY: field.y
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (!pageRef.current) return;
    
    // Handle dragging
    if (isDragging && selectedField) {
      const rect = pageRef.current.getBoundingClientRect();
      const field = fields.find(f => f.id === selectedField);
      if (!field) return;
      
      const newX = Math.max(0, Math.min((e.clientX - dragStart.x) / scale, (rect.width / scale) - field.width));
      const newY = Math.max(0, Math.min((e.clientY - dragStart.y) / scale, (rect.height / scale) - field.height));
      
      setFields(prev => prev.map(f => 
        f.id === selectedField ? { ...f, x: newX, y: newY } : f
      ));
    }
    
    // Handle resizing
    if (isResizing && selectedField && resizeDirection) {
      const deltaX = (e.clientX - resizeStart.x) / scale;
      const deltaY = (e.clientY - resizeStart.y) / scale;
      
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.fieldX;
      let newY = resizeStart.fieldY;
      
      // Handle different resize directions - allow very small sizes (min 10px)
      if (resizeDirection.includes('e')) {
        newWidth = Math.max(10, resizeStart.width + deltaX);
      }
      if (resizeDirection.includes('w')) {
        const widthDelta = Math.min(deltaX, resizeStart.width - 10);
        newWidth = resizeStart.width - widthDelta;
        newX = resizeStart.fieldX + widthDelta;
      }
      if (resizeDirection.includes('s')) {
        newHeight = Math.max(10, resizeStart.height + deltaY);
      }
      if (resizeDirection.includes('n')) {
        const heightDelta = Math.min(deltaY, resizeStart.height - 10);
        newHeight = resizeStart.height - heightDelta;
        newY = resizeStart.fieldY + heightDelta;
      }
      
      setFields(prev => prev.map(f => 
        f.id === selectedField ? { ...f, width: newWidth, height: newHeight, x: newX, y: newY } : f
      ));
    }
  }, [isDragging, isResizing, selectedField, dragStart, resizeStart, resizeDirection, scale, fields]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Delete selected field
  const deleteField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (selectedField === fieldId) setSelectedField(null);
  };

  // Update field properties
  const updateField = (fieldId, updates) => {
    setFields(prev => prev.map(f => 
      f.id === fieldId ? { ...f, ...updates } : f
    ));
  };

  // Save template
  const handleSaveTemplate = () => {
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }
    if (!pdfData) {
      alert('Please upload a PDF file');
      return;
    }
    if (fields.length === 0) {
      alert('Please add at least one field to the template');
      return;
    }

    const template = {
      id: existingTemplate?.id || `template_${Date.now()}`,
      name: templateName,
      pdfData: pdfData,
      fields: fields,
      numPages: numPages,
      preparerId: preparerId,
      companyId: companyId,
      createdAt: existingTemplate?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    onSave(template);
  };

  // Get fields for current page
  const currentPageFields = fields.filter(f => f.page === currentPage);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={onClose} style={styles.backButton}>← Back</button>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name..."
            style={styles.templateNameInput}
          />
        </div>
        <div style={styles.headerRight}>
          <button onClick={handleSaveTemplate} style={styles.saveButton}>
            💾 Save Template
          </button>
        </div>
      </div>

      <div style={styles.mainContent}>
        {/* Left Sidebar - Field Types */}
        <div style={styles.leftSidebar}>
          <h3 style={styles.sidebarTitle}>📋 Add Fields</h3>
          <p style={styles.sidebarHint}>Click to add a field, then drag to position</p>
          
          <div style={styles.fieldTypeList}>
            {fieldTypes.map((ft) => (
              <button
                key={ft.type}
                onClick={() => addField(ft)}
                style={styles.fieldTypeButton}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = ft.color;
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateX(4px)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${ft.color}50`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${ft.color}15`;
                  e.currentTarget.style.color = '#1e293b';
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ 
                  ...styles.fieldTypeIconBox, 
                  backgroundColor: ft.color,
                  boxShadow: `0 2px 8px ${ft.color}40`
                }}>
                  <span style={styles.fieldTypeIcon}>{ft.icon}</span>
                </div>
                <div style={styles.fieldTypeInfo}>
                  <span style={styles.fieldTypeName}>{ft.label}</span>
                  <span style={styles.fieldTypeHint}>
                    {ft.type === 'text' && 'Any text input'}
                    {ft.type === 'signature' && 'Draw signature'}
                    {ft.type === 'date' && 'Date picker'}
                    {ft.type === 'checkbox' && 'Yes/No check'}
                    {ft.type === 'initials' && 'Short signature'}
                    {ft.type === 'name' && 'Full name'}
                    {ft.type === 'email' && 'Email address'}
                    {ft.type === 'phone' && 'Phone number'}
                    {ft.type === 'ssn' && 'Last 4 digits'}
                    {ft.type === 'address' && 'Street address'}
                  </span>
                </div>
                <span style={styles.addIcon}>+</span>
              </button>
            ))}
          </div>
        </div>

        {/* Center - PDF Viewer */}
        <div style={styles.centerContent} ref={containerRef}>
          {!pdfData ? (
            <div style={styles.uploadArea}>
              <div style={styles.uploadBox}>
                <span style={styles.uploadIcon}>📄</span>
                <h3>Upload Your PDF Template</h3>
                <p>Drag and drop or click to select</p>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileUpload}
                  style={styles.fileInput}
                  id="pdf-upload"
                />
                <label htmlFor="pdf-upload" style={styles.uploadButton}>
                  Choose PDF File
                </label>
              </div>
            </div>
          ) : (
            <div style={styles.pdfContainer}>
              {/* Page Controls */}
              <div style={styles.pageControls}>
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  style={styles.pageButton}
                >
                  ← Prev
                </button>
                <span style={styles.pageInfo}>
                  Page {currentPage} of {numPages || '?'}
                </span>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage >= numPages}
                  style={styles.pageButton}
                >
                  Next →
                </button>
                <span style={styles.zoomControls}>
                  <button onClick={() => setScale(s => Math.max(0.5, s - 0.1))} style={styles.zoomButton}>−</button>
                  <span>{Math.round(scale * 100)}%</span>
                  <button onClick={() => setScale(s => Math.min(2, s + 0.1))} style={styles.zoomButton}>+</button>
                </span>
              </div>

              {/* PDF with Field Overlays */}
              <div style={styles.pdfWrapper} ref={pageRef}>
                {pdfLoading && (
                  <div style={styles.loading}>Loading PDF...</div>
                )}
                {pdfError && (
                  <div style={{ ...styles.loading, color: '#ef4444' }}>{pdfError}</div>
                )}
                {!pdfLoading && !pdfError && (
                  <Document
                    file={pdfData}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={<div style={styles.loading}>Rendering PDF...</div>}
                    error={<div style={{ ...styles.loading, color: '#ef4444' }}>Failed to load PDF</div>}
                  >
                    <Page 
                      pageNumber={currentPage} 
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                )}

                {/* Field Overlays */}
                {currentPageFields.map((field) => (
                  <div
                    key={field.id}
                    style={{
                      ...styles.fieldOverlay,
                      left: field.x * scale,
                      top: field.y * scale,
                      width: field.width * scale,
                      height: field.height * scale,
                      backgroundColor: field.filledBy === 'preparer' ? 'rgba(139, 92, 246, 0.3)' : `${field.color}30`,
                      outline: selectedField === field.id ? `2px solid ${field.filledBy === 'preparer' ? '#8b5cf6' : field.color}` : 'none',
                      cursor: isDragging && selectedField === field.id ? 'grabbing' : 'grab'
                    }}
                    onMouseDown={(e) => handleFieldMouseDown(e, field.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedField(field.id);
                    }}
                  >
                    {/* Required indicator */}
                    {field.required && (
                      <span style={{
                        position: 'absolute',
                        top: '-6px',
                        left: '-6px',
                        width: '14px',
                        height: '14px',
                        backgroundColor: '#ef4444',
                        borderRadius: '50%',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 15
                      }}>*</span>
                    )}
                    {/* Only show label if field is large enough */}
                    {field.width * scale > 40 && field.height * scale > 15 && (
                      <span style={{
                        ...styles.fieldLabel,
                        backgroundColor: field.filledBy === 'preparer' ? 'rgba(139, 92, 246, 0.9)' : 'rgba(255,255,255,0.9)',
                        color: field.filledBy === 'preparer' ? 'white' : 'inherit',
                        fontSize: Math.min(11, field.height * scale * 0.4) + 'px',
                        padding: '1px 3px'
                      }}>
                        {field.filledBy === 'preparer' ? '📋' : '👤'}{field.width * scale > 80 ? ` ${field.label}` : ''}
                      </span>
                    )}
                    {selectedField === field.id && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteField(field.id);
                          }}
                          style={styles.deleteFieldButton}
                        >
                          ×
                        </button>
                        
                        {/* Resize Handles */}
                        {/* Corner handles */}
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeNW }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 'nw')}
                        />
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeNE }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 'ne')}
                        />
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeSW }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 'sw')}
                        />
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeSE }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 'se')}
                        />
                        
                        {/* Edge handles */}
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeN }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 'n')}
                        />
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeS }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 's')}
                        />
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeE }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 'e')}
                        />
                        <div
                          style={{ ...styles.resizeHandle, ...styles.resizeW }}
                          onMouseDown={(e) => handleResizeMouseDown(e, field.id, 'w')}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Field Properties */}
        <div style={styles.rightSidebar}>
          <h3 style={styles.sidebarTitle}>⚙️ Field Properties</h3>
          
          {selectedField ? (
            <div style={styles.propertiesPanel}>
              {(() => {
                const field = fields.find(f => f.id === selectedField);
                if (!field) return null;
                
                return (
                  <>
                    <div style={styles.propertyGroup}>
                      <label style={styles.propertyLabel}>Label</label>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(field.id, { label: e.target.value })}
                        style={styles.propertyInput}
                      />
                    </div>
                    
                    <div style={styles.propertyGroup}>
                      <label style={styles.propertyLabel}>Placeholder</label>
                      <input
                        type="text"
                        value={field.placeholder || ''}
                        onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                        style={styles.propertyInput}
                      />
                    </div>

                    <div style={styles.propertyGroup}>
                      <label style={styles.propertyLabel}>Width</label>
                      <input
                        type="number"
                        value={field.width}
                        onChange={(e) => updateField(field.id, { width: parseInt(e.target.value) || 100 })}
                        style={styles.propertyInput}
                      />
                    </div>

                    <div style={styles.propertyGroup}>
                      <label style={styles.propertyLabel}>Height</label>
                      <input
                        type="number"
                        value={field.height}
                        onChange={(e) => updateField(field.id, { height: parseInt(e.target.value) || 25 })}
                        style={styles.propertyInput}
                      />
                    </div>

                    {/* Required/Optional Toggle */}
                    <div style={styles.propertyGroup}>
                      <label style={styles.propertyLabel}>Field Requirement</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => updateField(field.id, { required: true })}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: field.required ? '2px solid #ef4444' : '1px solid #e2e8f0',
                            borderRadius: '6px',
                            backgroundColor: field.required ? '#fef2f2' : 'white',
                            color: field.required ? '#dc2626' : '#64748b',
                            cursor: 'pointer',
                            fontWeight: field.required ? '600' : '400',
                            fontSize: '12px'
                          }}
                        >
                          ⚠️ Required
                        </button>
                        <button
                          onClick={() => updateField(field.id, { required: false })}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: !field.required ? '2px solid #10b981' : '1px solid #e2e8f0',
                            borderRadius: '6px',
                            backgroundColor: !field.required ? '#f0fdf4' : 'white',
                            color: !field.required ? '#059669' : '#64748b',
                            cursor: 'pointer',
                            fontWeight: !field.required ? '600' : '400',
                            fontSize: '12px'
                          }}
                        >
                          ✓ Optional
                        </button>
                      </div>
                    </div>

                    {/* Filled By Option */}
                    <div style={styles.propertyGroup}>
                      <label style={styles.propertyLabel}>Filled By</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => updateField(field.id, { filledBy: 'client' })}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: field.filledBy === 'client' ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                            borderRadius: '6px',
                            backgroundColor: field.filledBy === 'client' ? '#eff6ff' : 'white',
                            color: field.filledBy === 'client' ? '#1d4ed8' : '#64748b',
                            cursor: 'pointer',
                            fontWeight: field.filledBy === 'client' ? '600' : '400',
                            fontSize: '12px'
                          }}
                        >
                          👤 Client
                        </button>
                        <button
                          onClick={() => updateField(field.id, { filledBy: 'preparer' })}
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: field.filledBy === 'preparer' ? '2px solid #8b5cf6' : '1px solid #e2e8f0',
                            borderRadius: '6px',
                            backgroundColor: field.filledBy === 'preparer' ? '#f5f3ff' : 'white',
                            color: field.filledBy === 'preparer' ? '#6d28d9' : '#64748b',
                            cursor: 'pointer',
                            fontWeight: field.filledBy === 'preparer' ? '600' : '400',
                            fontSize: '12px'
                          }}
                        >
                          📋 Preparer
                        </button>
                      </div>
                    </div>

                    <button
                      onClick={() => deleteField(field.id)}
                      style={styles.deleteButton}
                    >
                      🗑️ Delete Field
                    </button>
                  </>
                );
              })()}
            </div>
          ) : (
            <p style={styles.noSelection}>Select a field to edit its properties</p>
          )}

          {/* Fields Summary */}
          <div style={styles.fieldsSummary}>
            <h4 style={styles.summaryTitle}>📑 All Fields ({fields.length})</h4>
            
            {/* Summary counts */}
            {fields.length > 0 && (
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', fontSize: '11px' }}>
                <span style={{ padding: '4px 8px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '4px' }}>
                  👤 Client: {fields.filter(f => f.filledBy !== 'preparer').length}
                </span>
                <span style={{ padding: '4px 8px', background: '#f5f3ff', color: '#6d28d9', borderRadius: '4px' }}>
                  📋 Preparer: {fields.filter(f => f.filledBy === 'preparer').length}
                </span>
              </div>
            )}
            
            {fields.length === 0 ? (
              <p style={styles.noFields}>No fields added yet</p>
            ) : (
              <div style={styles.fieldsList}>
                {fields.map((field, idx) => (
                  <div
                    key={field.id}
                    style={{
                      ...styles.fieldItem,
                      backgroundColor: selectedField === field.id ? (field.filledBy === 'preparer' ? '#f5f3ff' : `${field.color}20`) : 'transparent',
                      borderLeftColor: field.filledBy === 'preparer' ? '#8b5cf6' : field.color
                    }}
                    onClick={() => {
                      setSelectedField(field.id);
                      setCurrentPage(field.page);
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '10px' }}>{field.filledBy === 'preparer' ? '📋' : '👤'}</span>
                      {idx + 1}. {field.label}
                    </span>
                    <span style={styles.fieldPage}>Pg {field.page}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#f1f5f9',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: '#1e293b',
    color: 'white',
    boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  templateNameInput: {
    padding: '10px 15px',
    fontSize: '16px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: '#334155',
    color: 'white',
    width: '300px'
  },
  saveButton: {
    padding: '10px 20px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px'
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  leftSidebar: {
    width: '220px',
    backgroundColor: 'white',
    borderRight: '1px solid #e2e8f0',
    padding: '20px',
    overflowY: 'auto'
  },
  rightSidebar: {
    width: '280px',
    backgroundColor: 'white',
    borderLeft: '1px solid #e2e8f0',
    padding: '20px',
    overflowY: 'auto'
  },
  sidebarTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#1e293b'
  },
  sidebarHint: {
    fontSize: '12px',
    color: '#64748b',
    marginBottom: '15px',
    lineHeight: '1.4'
  },
  fieldTypeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  fieldTypeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: '#f8fafc',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s ease',
    textAlign: 'left',
    position: 'relative'
  },
  fieldTypeIconBox: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  fieldTypeIcon: {
    fontSize: '18px',
    filter: 'brightness(0) invert(1)'
  },
  fieldTypeInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  fieldTypeName: {
    fontWeight: '600',
    fontSize: '13px',
    color: '#1e293b'
  },
  fieldTypeHint: {
    fontSize: '10px',
    color: '#94a3b8'
  },
  addIcon: {
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    flexShrink: 0
  },
  centerContent: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#94a3b8'
  },
  uploadArea: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    width: '100%'
  },
  uploadBox: {
    backgroundColor: 'white',
    padding: '60px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  uploadIcon: {
    fontSize: '64px',
    marginBottom: '20px',
    display: 'block'
  },
  fileInput: {
    display: 'none'
  },
  uploadButton: {
    display: 'inline-block',
    marginTop: '20px',
    padding: '12px 30px',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '600'
  },
  pdfContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '15px',
    padding: '10px 20px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  pageButton: {
    padding: '6px 12px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px'
  },
  pageInfo: {
    fontSize: '14px',
    fontWeight: '500'
  },
  zoomControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: '20px',
    padding: '4px 10px',
    backgroundColor: '#f1f5f9',
    borderRadius: '4px'
  },
  zoomButton: {
    width: '28px',
    height: '28px',
    border: 'none',
    backgroundColor: '#e2e8f0',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  pdfWrapper: {
    position: 'relative',
    backgroundColor: 'white',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
  },
  loading: {
    padding: '100px',
    fontSize: '18px',
    color: '#64748b'
  },
  fieldOverlay: {
    position: 'absolute',
    border: 'none',
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '500',
    userSelect: 'none',
    transition: 'box-shadow 0.2s'
  },
  fieldLabel: {
    padding: '2px 6px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: '3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '90%'
  },
  deleteFieldButton: {
    position: 'absolute',
    top: '-10px',
    right: '-10px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  propertiesPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  propertyGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
  },
  propertyLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#475569'
  },
  propertyInput: {
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  deleteButton: {
    padding: '10px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '500',
    marginTop: '10px'
  },
  noSelection: {
    color: '#94a3b8',
    fontSize: '14px',
    textAlign: 'center',
    padding: '20px'
  },
  fieldsSummary: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '1px solid #e2e8f0'
  },
  summaryTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#1e293b'
  },
  noFields: {
    fontSize: '13px',
    color: '#94a3b8',
    textAlign: 'center'
  },
  fieldsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    maxHeight: '200px',
    overflowY: 'auto'
  },
  fieldItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    borderRadius: '4px',
    borderLeft: '3px solid',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'background-color 0.2s'
  },
  fieldPage: {
    fontSize: '11px',
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    padding: '2px 6px',
    borderRadius: '3px'
  },
  // Resize handle base style
  resizeHandle: {
    position: 'absolute',
    width: '10px',
    height: '10px',
    backgroundColor: 'white',
    border: '2px solid #3b82f6',
    borderRadius: '2px',
    zIndex: 10
  },
  // Corner handles
  resizeNW: {
    top: '-5px',
    left: '-5px',
    cursor: 'nw-resize'
  },
  resizeNE: {
    top: '-5px',
    right: '-5px',
    cursor: 'ne-resize'
  },
  resizeSW: {
    bottom: '-5px',
    left: '-5px',
    cursor: 'sw-resize'
  },
  resizeSE: {
    bottom: '-5px',
    right: '-5px',
    cursor: 'se-resize'
  },
  // Edge handles
  resizeN: {
    top: '-5px',
    left: '50%',
    transform: 'translateX(-50%)',
    cursor: 'n-resize'
  },
  resizeS: {
    bottom: '-5px',
    left: '50%',
    transform: 'translateX(-50%)',
    cursor: 's-resize'
  },
  resizeE: {
    top: '50%',
    right: '-5px',
    transform: 'translateY(-50%)',
    cursor: 'e-resize'
  },
  resizeW: {
    top: '50%',
    left: '-5px',
    transform: 'translateY(-50%)',
    cursor: 'w-resize'
  }
};

export default PDFDocumentBuilder;
